import { createRoot } from "react-dom/client";
import App from "./App.tsx";
// Self-hosted fonts (eliminates render-blocking Google Fonts request).
// Trimmed to weights actually used above-the-fold; rarer weights fall back to
// the next available weight (browsers synthesize gracefully).
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/plus-jakarta-sans/400.css";
import "@fontsource/plus-jakarta-sans/600.css";
import "@fontsource/plus-jakarta-sans/700.css";
import "@fontsource/plus-jakarta-sans/800.css";
import "./index.css";
import { initSentry, Sentry } from "@/lib/sentry";

// Stale-chunk recovery for atomic SPA deploys. After a deploy, a tab with the
// previous index.html still references old hashed chunk filenames. When the
// user clicks a lazy route, ONE of three failure modes fires depending on
// browser + CDN behaviour:
//
//   (a) Network 404 / parse failure → `vite:preloadError` event.
//   (b) Unhandled rejection of the dynamic-import promise (browsers that
//       don't dispatch (a), or our own React.lazy() wrappers that don't
//       hook into Vite's preload).
//   (c) Promise resolves with an UNDEFINED module — happens when the CDN
//       (Cloudflare Pages here) serves index.html as a SPA fallback for the
//       missing /assets/*.js, returning 200 + text/html. The import parses
//       the HTML as JS, gets `undefined`, React.lazy crashes inside Suspense
//       reading `.default`. Caught synchronously by the ErrorBoundary fallback
//       below, NOT by the two listeners here (it's not a fetch error or a
//       rejection — it's a runtime TypeError during render).
//
// All three paths reload-once. A sessionStorage guard prevents a genuinely
// broken chunk from reload-looping.
const STALE_CHUNK_RELOAD_KEY = "nlpd:stale-chunk-reloaded";
const STALE_LAZY_PATTERN =
  /Cannot read propert(?:ies|y) of undefined.*default|Loading chunk \d+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i;

export function tryStaleChunkReload(message: string): boolean {
  if (typeof window === "undefined") return false;
  if (!STALE_LAZY_PATTERN.test(message)) return false;
  if (sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) === "1") return false;
  sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, "1");
  window.location.reload();
  return true;
}

if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    if (sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) === "1") return;
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, "1");
    event.preventDefault();
    window.location.reload();
  });
  window.addEventListener("unhandledrejection", (event) => {
    const msg = String(
      (event.reason as { message?: string } | undefined)?.message ?? event.reason ?? "",
    );
    if (tryStaleChunkReload(msg)) event.preventDefault();
  });
  // Clear the flag once the page successfully loaded (i.e. we got past first paint).
  window.addEventListener("load", () => {
    sessionStorage.removeItem(STALE_CHUNK_RELOAD_KEY);
  });
}

// Defer Sentry init until the browser is idle so it doesn't compete with
// LCP/FCP critical work.
const scheduleIdle =
  (typeof window !== "undefined" &&
    (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void })
      .requestIdleCallback) ||
  ((cb: () => void) => setTimeout(cb, 2000));
scheduleIdle(() => initSentry(), { timeout: 4000 });

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => {
      // Path (c) of stale-chunk recovery: React.lazy() resolved with an
      // undefined module (CF Pages served index.html as fallback for a
      // missing /assets/*.js) → reading `.default` threw here. Auto-reload
      // once before showing the error UI. tryStaleChunkReload no-ops on
      // unrelated errors and on the second attempt in the same session.
      const message = error instanceof Error ? error.message : String(error);
      if (tryStaleChunkReload(message)) {
        return (
          <div style={{ fontFamily: "Inter, sans-serif", textAlign: "center", padding: 40, color: "#64748b" }}>
            Mise à jour de l'application…
          </div>
        );
      }
      return (
      <div
        style={{
          fontFamily: "-apple-system, Segoe UI, Inter, sans-serif",
          maxWidth: 600,
          margin: "60px auto",
          padding: 24,
          textAlign: "center",
          color: "#0f172a",
        }}
      >
        <h1 style={{ fontSize: 22, marginBottom: 12 }}>
          Une erreur inattendue s'est produite
        </h1>
        <p style={{ color: "#475569", marginBottom: 16, lineHeight: 1.5 }}>
          Nos équipes ont été automatiquement notifiées. Vous pouvez réessayer
          ou rafraîchir la page.
        </p>
        <details
          style={{
            textAlign: "left",
            background: "#f8fafc",
            padding: 12,
            borderRadius: 6,
            fontSize: 12,
            color: "#64748b",
            marginBottom: 16,
          }}
        >
          <summary style={{ cursor: "pointer" }}>Détails techniques</summary>
          <code style={{ wordBreak: "break-word" }}>
            {error instanceof Error ? error.message : String(error)}
          </code>
        </details>
        <button
          onClick={resetError}
          style={{
            background: "#0066cc",
            color: "#fff",
            border: "none",
            padding: "10px 20px",
            borderRadius: 6,
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Réessayer
        </button>
      </div>
      );
    }}
  >
    <App />
  </Sentry.ErrorBoundary>,
);
